import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  BarChart3, TrendingUp, Clock, Euro, RefreshCw, 
  ChevronLeft, ChevronRight, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SalesChartPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/ventas/hourly?fecha=${selectedDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching hourly data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  // Find max value for scaling
  const getMaxValue = () => {
    if (!data?.hourly_data) return 100;
    const values = Object.values(data.hourly_data).map(h => h.ingresos);
    return Math.max(...values, 50);
  };

  // Find peak hours
  const getPeakHours = () => {
    if (!data?.hourly_data) return [];
    const entries = Object.entries(data.hourly_data)
      .filter(([_, v]) => v.ingresos > 0)
      .sort((a, b) => b[1].ingresos - a[1].ingresos);
    return entries.slice(0, 3);
  };

  const maxValue = getMaxValue();
  const peakHours = getPeakHours();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-stone-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Análisis por Horas</h2>
          <p className="text-sm text-gray-500 hidden md:block">Distribución de ventas a lo largo del día</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="flex items-center gap-2">
          <RefreshCw size={16} />
          <span className="hidden sm:inline">Actualizar</span>
        </Button>
      </div>

      {/* Date Navigation - Mobile Optimized */}
      <div className="bg-white rounded-xl p-3 shadow-sm border">
        <div className="flex items-center justify-between gap-2">
          <Button variant="outline" size="sm" onClick={() => changeDate(-1)} className="px-2">
            <ChevronLeft size={18} />
          </Button>
          <div className="flex-1 text-center">
            <p className="font-medium text-sm md:text-base capitalize truncate">
              {formatDate(selectedDate)}
            </p>
          </div>
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-10 md:w-auto opacity-0 absolute md:relative md:opacity-100"
          />
          <Button 
            variant="outline" 
            size="sm"
            className="px-2"
            onClick={() => changeDate(1)}
            disabled={selectedDate >= new Date().toISOString().split('T')[0]}
          >
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>

      {/* Summary Stats - Mobile Optimized */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl p-4 shadow-sm border text-center">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
            <Euro className="text-green-600" size={20} />
          </div>
          <p className="text-xl md:text-2xl font-bold text-gray-900">{data?.total_ingresos?.toFixed(0) || 0}€</p>
          <p className="text-xs text-gray-500">Ingresos</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border text-center">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
            <BarChart3 className="text-blue-600" size={20} />
          </div>
          <p className="text-xl md:text-2xl font-bold text-gray-900">{data?.total_ventas || 0}</p>
          <p className="text-xs text-gray-500">Ventas</p>
        </div>
        {peakHours[0] && (
          <>
            <div className="bg-amber-50 rounded-xl p-4 shadow-sm border border-amber-200 text-center">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <TrendingUp className="text-amber-600" size={20} />
              </div>
              <p className="text-xl md:text-2xl font-bold text-amber-700">{peakHours[0][0]}:00</p>
              <p className="text-xs text-amber-600">Hora punta</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border text-center">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Clock className="text-purple-600" size={20} />
              </div>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{peakHours[0][1].ingresos.toFixed(0)}€</p>
              <p className="text-xs text-gray-500">En punta</p>
            </div>
          </>
        )}
      </div>

      {/* Hourly Chart */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 size={18} />
            Ingresos por hora
          </h3>
        </div>
        <div className="p-4">
          <div className="relative">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 bottom-8 w-8 md:w-12 flex flex-col justify-between text-xs text-gray-400">
              <span>{maxValue.toFixed(0)}€</span>
              <span>{(maxValue * 0.5).toFixed(0)}€</span>
              <span>0€</span>
            </div>
            
            {/* Chart */}
            <div className="ml-10 md:ml-14 flex items-end gap-px md:gap-1 h-48 md:h-64">
              {data?.hourly_data && Object.entries(data.hourly_data).map(([hour, values]) => {
                const height = maxValue > 0 ? (values.ingresos / maxValue) * 100 : 0;
                const isPeak = peakHours.some(([h]) => h === hour);
                
                return (
                  <div key={hour} className="flex-1 flex flex-col items-center group">
                    {/* Bar */}
                    <div 
                      className="relative w-full transition-all duration-300"
                      style={{ height: `${Math.max(height, 2)}%` }}
                    >
                      <div 
                        className={`absolute bottom-0 w-full rounded-t transition-colors ${
                          isPeak 
                            ? 'bg-amber-500 hover:bg-amber-400' 
                            : values.ingresos > 0 
                            ? 'bg-green-500 hover:bg-green-400'
                            : 'bg-gray-200'
                        }`}
                        style={{ height: '100%' }}
                      />
                    </div>
                    
                    {/* X-axis label - only show some on mobile */}
                    <span className={`text-xs mt-1 ${
                      isPeak ? 'font-bold text-amber-600' : 'text-gray-400'
                    } ${parseInt(hour) % 3 !== 0 ? 'hidden md:block' : ''}`}>
                      {hour}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-500" />
              <span className="text-xs text-gray-600">Normal</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-amber-500" />
              <span className="text-xs text-gray-600">Punta</span>
            </div>
          </div>
        </div>
      </div>

      {/* Peak Hours Detail - Mobile Optimized */}
      {peakHours.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-900">Horas de mayor actividad</h3>
          </div>
          <div className="p-3">
            <div className="space-y-2">
              {peakHours.map(([hour, values], idx) => (
                <div 
                  key={hour}
                  className={`p-3 rounded-xl flex items-center justify-between ${
                    idx === 0 ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl font-bold ${idx === 0 ? 'text-amber-700' : 'text-gray-700'}`}>
                      {hour}:00
                    </span>
                    {idx === 0 && (
                      <span className="text-xs font-bold text-amber-600 bg-amber-200 px-2 py-1 rounded">
                        #1
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-600">{values.ingresos.toFixed(0)}€</p>
                    <p className="text-xs text-gray-500">{values.ventas} ventas</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {data?.total_ventas === 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <BarChart3 size={40} className="mx-auto text-gray-300 mb-3" />
          <h3 className="font-medium text-gray-900 mb-1">Sin ventas</h3>
          <p className="text-sm text-gray-500">No hay datos para esta fecha</p>
        </div>
      )}
    </div>
  );
};

export default SalesChartPanel;
